import { observer } from 'mobx-react-lite';
import getSeeMoreButton from './getSeeMoreButton';
import { default as RelatedEvent } from './RelatedEvent';
import { relatedContentEventsHeader } from './RelatedEventsWell.locstring.json';
import { logSeeMoreClicked } from '../helpers/instrumentationHelpers';
import type { ReadingPaneEntrySourceType } from 'owa-calendar-helpers-types/lib/ReadingPaneEntrySourceType';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import loc from 'owa-localize';
import { EventInsightType, InsightsDataFetchSource } from 'owa-meeting-insights-types-and-flights';
import { getInsightsTraceId, getRelatedEvents } from 'owa-meeting-insights';
import * as React from 'react';
import { MAX_CONTENT_TO_SHOW } from 'owa-meeting-insights/lib/selectors/meetingInsightsConsts';
import { useInsightsWidthHelper } from '../helpers/useInsightsWidthHelper';
import { FeedbackType } from './FeedbackContainer';

import styles from './RelatedEventsWell.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface RelatedEventsWellProps {
    adjustTopMargin: boolean;
    color: string;
    colorScheme: ColorScheme;
    meetingId: string;
    readingPaneEntrySource?: ReadingPaneEntrySourceType; // null when rendering from CITI card
    insightsEntrySource: InsightsDataFetchSource;
}

export interface RelatedEventsWellState {
    shouldShowAllEvents: boolean;
}

export default observer(function RelatedEventsWell(props: RelatedEventsWellProps) {
    const { meetingId, adjustTopMargin, insightsEntrySource } = props;
    const events = getRelatedEvents(meetingId, insightsEntrySource);

    const containerRef = React.useRef<HTMLDivElement>();
    const [shouldShowAllEvents, setShouldShowAllEvents] = React.useState<boolean>(false);
    const { insightsInOneRow, seeMoreButtonRightMargin } = useInsightsWidthHelper(
        containerRef,
        insightsEntrySource
    );
    const maxEventsToShow = Math.min(events.length, MAX_CONTENT_TO_SHOW);
    const renderRelatedEvents = (numEvents: number, events: EventInsightType[]): JSX.Element[] => {
        const relatedEventsToRender = [];
        for (let i = 0; i < numEvents; i++) {
            const event = events[i];
            relatedEventsToRender.push(
                <RelatedEvent
                    key={'relatedEvent_' + i}
                    meetingId={props.meetingId}
                    event={event}
                    color={props.color}
                    colorScheme={props.colorScheme}
                    readingPaneEntrySource={props.readingPaneEntrySource}
                    insightsEntrySource={insightsEntrySource}
                    position={i}
                    renderedEventsCount={numEvents}
                />
            );
        }
        return relatedEventsToRender;
    };
    const renderSeeMoreButton = (numRelatedEventsToRender: number): JSX.Element => {
        return maxEventsToShow > numRelatedEventsToRender && !shouldShowAllEvents
            ? getSeeMoreButton(
                  maxEventsToShow - numRelatedEventsToRender /* numberToSeeMore */,
                  props.colorScheme,
                  onSeeMoreClicked,
                  styles.seeMoreEventsButton,
                  seeMoreButtonRightMargin /* marginRight */
              )
            : null;
    };
    const onSeeMoreClicked = () => {
        const { meetingId, insightsEntrySource } = props;
        setShouldShowAllEvents(true);
        logSeeMoreClicked(
            getInsightsTraceId(props.meetingId, insightsEntrySource),
            meetingId,
            insightsEntrySource,
            FeedbackType.Event
        );
    };

    let numRelatedEventsToRender = Math.min(insightsInOneRow, events.length);
    if (shouldShowAllEvents) {
        numRelatedEventsToRender = maxEventsToShow;
    }
    return (
        <>
            <div
                className={classNames(
                    styles.relatedEventsWell,
                    adjustTopMargin && styles.marginTop
                )}
                ref={containerRef}
                role="list"
                aria-label={loc(relatedContentEventsHeader)}>
                <div className={styles.header}>{loc(relatedContentEventsHeader)}</div>
                {renderRelatedEvents(numRelatedEventsToRender, events)}
            </div>
            {renderSeeMoreButton(numRelatedEventsToRender)}
        </>
    );
});
